// Reusable modal dialog that can be used to display content in a modal overlay
// Pass in isOpen to control visibility, onRequestClose to handle closing the modal,
// and children to render the content inside the modal
// Also whether clicking outside the modal should close it (default true)
import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ModalDialogProps } from './modal-dialog.types';
import {
  Backdrop,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
} from './modal-dialog.styles';
import { useFocusTrap } from 'hooks/use-focus-trap/use-focus-trap';
import { SvgIcon } from 'components/elements/svg-icon/SvgIcon';
import CloseIcon from 'assets/icons/close.svg?react';

export const ModalDialog: React.FC<ModalDialogProps> = ({
  isOpen,
  onRequestClose,
  children,
  closeOnOutsideClick = true,
  title,
  showCloseButton = true,
}) => {
  const modalRef = useFocusTrap(isOpen);

  // Handle keyboard events (ESC to close)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onRequestClose();
      }
    },
    [onRequestClose]
  );

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOutsideClick && event.target === event.currentTarget) {
      onRequestClose();
    }
  };

  // Handle ESC key and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    // Add keyboard listener for ESC
    document.addEventListener('keydown', handleKeyDown);

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalContent = (
    <Backdrop onClick={handleBackdropClick}>
      <ModalContainer
        ref={modalRef}
        aria-modal='true'
        data-testid='modal-dialog'
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <ModalHeader>
            {title && <ModalTitle id='modal-title'>{title}</ModalTitle>}
            {showCloseButton && (
              <CloseButton
                onClick={onRequestClose}
                aria-label='TODO Close text'
                type='button'
              >
                <SvgIcon icon={CloseIcon} width={20} height={20} />
              </CloseButton>
            )}
          </ModalHeader>
        )}
        <ModalContent id='modal-content'>{children}</ModalContent>
      </ModalContainer>
    </Backdrop>
  );

  // Use portal to render modal at the end of the document body
  return ReactDOM.createPortal(modalContent, document.body);
};
